import * as React from "react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface LifeRemainingProgressProps {
  label: string;
  value: number; // 0-100 for percentage, or 0-10 for ratings
  max?: number; // 100 for percentage, 10 for ratings
  icon?: LucideIcon;
  showPercentage?: boolean;
  subtitle?: string; // Optional subtitle like "2,000 / 10,000 hrs"
  className?: string;
}

export const LifeRemainingProgress: React.FC<LifeRemainingProgressProps> = ({
  label,
  value,
  max = 100,
  icon: Icon,
  showPercentage = true,
  subtitle,
  className
}) => {
  // Normalize value to percentage
  const percentage = max === 100 ? value : (value / max) * 100;
  
  // Determine color based on percentage
  const getColorClasses = () => {
    if (percentage >= 70) {
      return {
        bg: "bg-green-500/10",
        bar: "bg-green-500",
        text: "text-green-700 dark:text-green-300"
      };
    } else if (percentage >= 40) {
      return {
        bg: "bg-yellow-500/10",
        bar: "bg-yellow-500",
        text: "text-yellow-700 dark:text-yellow-300"
      };
    } else {
      return {
        bg: "bg-red-500/10",
        bar: "bg-red-500",
        text: "text-red-700 dark:text-red-300"
      };
    }
  };

  const colors = getColorClasses();

  return (
    <div className={cn("flex items-start gap-2 p-2 rounded-md", colors.bg, className)}>
      {Icon && <Icon className={cn("w-3.5 h-3.5 mt-0.5 flex-shrink-0", colors.text)} />}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <div className={cn("text-[10px] uppercase font-medium", colors.text)}>
            {label}
          </div>
          <div className={cn("text-xs font-bold tabular-nums", colors.text)}>
            {showPercentage ? `${Math.round(percentage)}%` : `${value}/${max}`}
          </div>
        </div>
        {subtitle && (
          <div className={cn("text-[9px] truncate", colors.text, "opacity-80")}>
            {subtitle}
          </div>
        )}
        <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
          <div 
            className={cn("h-full transition-all duration-500 rounded-full", colors.bar)}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};
